import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Check, KeyRound, Loader2, PackageX, Send, ShieldAlert, X } from "lucide-react";
import { realtime } from "@/lib/realtime";

/**
 * Session 41 — Staff POS: what happens when an item can't be served from
 * the service location under the ingredient's fallback policy.
 *
 *   • "Do not fallback"   → OUT OF STOCK AT SERVICE LOCATION. No sale, no
 *                            BOH consumption. Staff must change the order.
 *   • "Require manager"   → MANAGER APPROVAL REQUIRED. Two ways to approve:
 *       1. Ask the Owner/Manager dashboard (Allow / Reject) — we wait here
 *          for the decision (cloud WS or LAN mesh, same bus) and poll as a
 *          safety net.
 *       2. A manager types the manager / void password on THIS device.
 *     Approval is scoped to table + ingredient + quantity and expires after
 *     15 minutes. It NEVER creates an order: staff must add the item again.
 *
 * Hidden (visibility OFF) items never reach this dialog — they are not
 * selectable at all, so no attempt, no fallback, no approval.
 */
const fmt = (n) => (n == null || !Number.isFinite(Number(n)) ? "—" : (Number.isInteger(Number(n)) ? String(Number(n)) : Number(n).toFixed(2).replace(/\.?0+$/, "")));
const errMsg = (e, fb) => { const d = e?.response?.data?.detail; return (typeof d === "string" ? d : d?.message) || fb; };

export const FallbackBlockDialog = ({ open, onOpenChange, block, http, authHeaders, tableSession, staffName, onApproved }) => {
  // block = { code, message, blocked: [status rows], hint, itemNames?: [] }
  const [requests, setRequests] = useState({}); // ingredient_id → approval doc
  const [mode, setMode] = useState("choose"); // choose | waiting | password | done
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState("");
  const [pwError, setPwError] = useState("");
  // Session 41.5 — on-device "Approve" can ALSO switch the ingredient to
  // "Service stock first, then fallback" for the rest of service.
  const [switchPolicy, setSwitchPolicy] = useState(false);
  const pwRef = useRef(null);
  const isOos = block?.code === "out_of_stock_at_service_location";
  const blocked = useMemo(() => (block?.blocked || []), [block]);
  const needsApproval = useMemo(() => blocked.filter((b) => b.status === "approval_required"), [blocked]);
  const hardOos = useMemo(() => blocked.filter((b) => b.status === "out_of_stock"), [blocked]);

  useEffect(() => { if (open) { setRequests({}); setMode("choose"); setPw(""); setPwError(""); setBusy(""); } }, [open, block]);

  const allDecided = Object.keys(requests).length > 0 && needsApproval.every((b) => ["approved", "rejected", "expired", "used"].includes(requests[b.ingredient_id]?.status));
  const allApproved = Object.keys(requests).length > 0 && needsApproval.every((b) => requests[b.ingredient_id]?.status === "approved");
  const anyRejected = Object.values(requests).some((a) => a.status === "rejected");

  // Live decisions (cloud WS or LAN mesh) + polling safety net while waiting.
  useEffect(() => {
    if (!open || mode !== "waiting") return undefined;
    const ids = new Set(Object.values(requests).map((a) => a.id));
    const unsub = realtime.subscribe((msg) => {
      if (msg?.type !== "fallback_approval_decided") return;
      const a = msg?.payload?.approval;
      if (a && ids.has(a.id)) setRequests((r) => ({ ...r, [a.ingredient_id]: a }));
    });
    const poll = async () => {
      for (const a of Object.values(requests)) {
        if (a.status !== "pending") continue;
        try { const r = await http.get(`/staff/fallback-approvals/${a.id}`, { headers: authHeaders }); if (r.data?.approval) setRequests((x) => ({ ...x, [a.ingredient_id]: r.data.approval })); } catch (_) { /* keep waiting */ }
      }
    };
    const iv = setInterval(poll, 5000);
    return () => { unsub(); clearInterval(iv); };
  }, [open, mode, requests, http, authHeaders]);

  useEffect(() => {
    if (mode === "waiting" && allDecided) {
      setMode("done");
      if (allApproved) { toast.success("Manager approved — add the item again to place the order."); onApproved?.(Object.values(requests)); }
      else toast.error(anyRejected ? "Manager rejected the request." : "The request expired.");
    }
  }, [mode, allDecided, allApproved, anyRejected, requests, onApproved]);

  const requestAll = async () => {
    setBusy("request");
    try {
      const out = {};
      for (const b of needsApproval) {
        const r = await http.post("/staff/fallback-approvals", {
          ingredient_id: b.ingredient_id, quantity: b.required, table_session_id: tableSession?.id || null, table_number: tableSession?.table_number != null ? String(tableSession.table_number) : null,
          menu_item_name: (block?.itemNames || []).join(", ") || null, staff_name: staffName || null,
        }, { headers: authHeaders });
        out[b.ingredient_id] = r.data.approval;
      }
      setRequests(out);
      return out;
    } catch (e) { toast.error(errMsg(e, "Couldn't send the request.")); return null; } finally { setBusy(""); }
  };

  const askDashboard = async () => {
    const out = Object.keys(requests).length ? requests : await requestAll();
    if (out) { setMode("waiting"); toast.info("Sent to the manager dashboard — waiting for a decision."); }
  };

  const openPassword = async () => {
    const out = Object.keys(requests).length ? requests : await requestAll();
    if (out) { setMode("password"); setTimeout(() => pwRef.current?.focus(), 60); }
  };

  const decideWithPassword = async () => {
    if (!pw.trim()) return;
    setBusy("pw"); setPwError("");
    try {
      const next = { ...requests };
      for (const a of Object.values(requests)) {
        if (a.status !== "pending") continue;
        const r = await http.post(`/staff/fallback-approvals/${a.id}/decide`, { action: "allow", manager_password: pw, switch_policy: !!switchPolicy }, { headers: authHeaders });
        next[a.ingredient_id] = r.data.approval;
      }
      setRequests(next);
      setMode("done");
      toast.success(switchPolicy ? "Approved — this ingredient now uses fallback stock automatically for the rest of service. Add the item again to place the order." : "Approved on this device — add the item again to place the order.");
      onApproved?.(Object.values(next));
    } catch (e) {
      const st = e?.response?.status;
      setPwError(st === 403 ? "Invalid manager password." : errMsg(e, "Couldn't approve."));
    } finally { setBusy(""); }
  };

  const row = (b) => {
    const a = requests[b.ingredient_id];
    return (
      <div key={b.ingredient_id} className="rounded-2xl border border-border bg-background p-3" data-testid={`fallback-block-row-${b.ingredient_id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold">{b.ingredient_name}</p>
            <p className="text-xs text-muted-foreground">Need <b className="text-foreground">{b.required_label || `${fmt(b.required)} ${b.unit || ""}`}</b> · at {b.preferred_location_name || "service location"}: <b className="text-foreground">{b.preferred_balance_label || `${fmt(b.preferred_balance)} ${b.unit || ""}`}</b>{b.fallback_available > 0 ? <> · elsewhere: <b className="text-foreground">{b.fallback_available_label || `${fmt(b.fallback_available)} ${b.unit || ""}`}</b></> : null}</p>
          </div>
          {a ? <Badge className={`shrink-0 rounded-full ${a.status === "approved" ? "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-300" : a.status === "pending" ? "bg-amber-500/15 text-amber-700 hover:bg-amber-500/15 dark:text-amber-300" : "bg-destructive/10 text-destructive hover:bg-destructive/10"}`} data-testid={`fallback-approval-status-${b.ingredient_id}`}>{a.status === "pending" ? "Waiting…" : a.status}</Badge>
            : <Badge variant="outline" className="shrink-0 rounded-full">{b.status === "out_of_stock" ? "No fallback" : "Manager approval"}</Badge>}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-[24px] sm:max-w-lg" data-testid="fallback-block-dialog">
        <DialogHeader>
          <div className={`mb-1 inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${isOos ? "bg-destructive/10 text-destructive" : "bg-amber-500/15 text-amber-700 dark:text-amber-300"}`} data-testid="fallback-block-code">
            {isOos ? <PackageX className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />} {isOos ? "Out of stock at service location" : "Manager approval required"}
          </div>
          <DialogTitle className="font-['Space_Grotesk'] text-2xl" data-testid="fallback-block-title">{isOos ? "This can't be sold right now" : "Ask a manager before using fallback stock"}</DialogTitle>
          <DialogDescription data-testid="fallback-block-hint">
            {isOos
              ? (hardOos.some((b) => b.fallback_available > 0) ? "Stock exists in another location, but automatic fallback is switched off for this ingredient. Nothing was ordered and no back-of-house stock was used." : "No stock is available at the service location. Nothing was ordered.")
              : "The order was NOT placed. Once a manager allows it, add the item again — approval is valid for this table, this ingredient and this quantity for 15 minutes."}
            {block?.itemNames?.length ? <> <span className="text-foreground">Items: {block.itemNames.join(", ")}.</span></> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">{blocked.map(row)}</div>

        {!isOos && hardOos.length ? <p className="flex items-start gap-1.5 text-xs text-destructive" data-testid="fallback-block-mixed"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Some ingredients are out of stock with no fallback allowed — those items can't be sold even with approval.</p> : null}

        {!isOos && mode === "choose" ? (
          <div className="grid gap-2 sm:grid-cols-2" data-testid="fallback-approval-options">
            <Button className="h-14 rounded-xl" disabled={!!busy || !needsApproval.length} onClick={askDashboard} data-testid="fallback-ask-dashboard">{busy === "request" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Ask manager dashboard</Button>
            <Button variant="outline" className="h-14 rounded-xl" disabled={!!busy || !needsApproval.length} onClick={openPassword} data-testid="fallback-manager-password">{busy === "request" ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Manager password here</Button>
          </div>
        ) : null}

        {mode === "waiting" ? (
          <div className="flex items-center gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="fallback-waiting">
            <Loader2 className="h-5 w-5 animate-spin text-amber-700 dark:text-amber-300" />
            <div><p className="font-semibold">Waiting for the manager…</p><p className="text-xs text-muted-foreground">Sent to the Owner/Manager dashboard. You can also enter the manager password here instead.</p></div>
            <Button variant="outline" className="ml-auto h-10 rounded-xl" onClick={() => { setMode("password"); setTimeout(() => pwRef.current?.focus(), 60); }} data-testid="fallback-switch-password"><KeyRound className="h-4 w-4" /></Button>
          </div>
        ) : null}

        {mode === "password" ? (
          <div className="space-y-2 rounded-2xl border border-border bg-secondary/40 p-3" data-testid="fallback-password-form">
            <Label className="text-xs">Manager / void password</Label>
            <Input ref={pwRef} type="password" inputMode="numeric" value={pw} onChange={(e) => { setPw(e.target.value); setPwError(""); }} onKeyDown={(e) => { if (e.key === "Enter") decideWithPassword(); }} className="h-12 rounded-xl text-lg tracking-widest" autoComplete="off" data-testid="fallback-password-input" aria-invalid={!!pwError} />
            {pwError ? <p className="text-xs font-medium text-destructive" data-testid="fallback-password-error">{pwError}</p> : null}
            <label className="flex min-h-11 cursor-pointer items-start gap-2 rounded-xl border border-border bg-background px-3 py-2 text-xs" data-testid="fallback-switch-policy-option">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[hsl(var(--accent))]" checked={switchPolicy} onChange={(e) => setSwitchPolicy(e.target.checked)} data-testid="fallback-switch-policy-checkbox" />
              <span><b>Also switch to “Service stock first, then fallback” for today</b><span className="block text-muted-foreground">Nobody gets blocked on this ingredient again this service. The manager can reset it under Inventory → When service stock runs out.</span></span>
            </label>
            <div className="flex gap-2">
              <Button className="h-12 flex-1 rounded-xl" disabled={busy === "pw" || !pw.trim()} onClick={decideWithPassword} data-testid="fallback-password-submit">{busy === "pw" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve</Button>
              <Button variant="ghost" className="h-12 rounded-xl" onClick={() => setMode(Object.keys(requests).length ? "waiting" : "choose")} data-testid="fallback-password-back">Back</Button>
            </div>
          </div>
        ) : null}

        {mode === "done" ? (
          <div className={`rounded-2xl border p-3 text-sm ${allApproved ? "border-emerald-500/40 bg-emerald-500/10" : "border-destructive/40 bg-destructive/10"}`} data-testid="fallback-decision">
            <p className="font-semibold">{allApproved ? "Approved — now add the item again." : anyRejected ? "Rejected by the manager." : "Request expired."}</p>
            <p className="text-xs text-muted-foreground">{allApproved ? "Nothing was ordered automatically. Re-add the item to this table within 15 minutes and it will go through." : "Nothing was ordered. Change the order or try again later."}</p>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant={isOos || mode === "done" ? "default" : "outline"} className="h-12 rounded-xl" onClick={() => onOpenChange(false)} data-testid="fallback-block-close"><X className="h-4 w-4" /> {isOos ? "Back to order" : mode === "done" ? "Close" : "Cancel"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Small tile badge for menu items whose recipe can't be served from the service location. */
export const ServiceAvailabilityBadge = ({ availability, className = "" }) => {
  if (!availability || !availability.status || availability.status === "available") return null;
  const st = availability.status;
  const cls = st === "out_of_stock" ? "bg-destructive/10 text-destructive" : st === "approval_required" ? "bg-amber-500/15 text-amber-700 dark:text-amber-300" : "bg-secondary text-muted-foreground";
  const label = availability.label || (st === "out_of_stock" ? "Out of stock at service location" : st === "approval_required" ? "Manager approval" : st === "available_via_fallback" ? "Uses fallback stock" : "Service stock low");
  return <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls} ${className}`} data-testid="service-availability-badge">{st === "out_of_stock" ? <PackageX className="h-3 w-3" /> : st === "approval_required" ? <ShieldAlert className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}{label}</span>;
};
